import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useCallOnce from "../hooks/useCallOnce";
import {
  getDefaultPrompt,
  saveGlobalPrompt,
} from "../redux/actions/defaultPromptActions";
import {
  getDefaultVoicePrompt,
} from "../redux/actions/defaultVoicePromptActions";
import {
  saveGlobalVoicePrompt,
} from "../redux/actions/globalVoicePromptActions";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function GlobalPrompt() {
  const dispatch = useDispatch();
  const { prompt, globalPrompt } = useSelector((s) => s.defaultPrompt);
  const defaultVoicePrompt = useSelector((s) => s.defaultVoicePrompt);
  const globalVoicePrompt = useSelector((s) => s.globalVoicePrompt);

  const [activeTab, setActiveTab] = useState(0);
  const [promptText, setPromptText] = useState("");
  const [voicePromptText, setVoicePromptText] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");

  // Fetch simple prompt on component mount
  useCallOnce(
    "globalprompt/init",
    () => {
      dispatch(getDefaultPrompt());
    },
    [dispatch]
  );

  // Fetch voice prompt when voice tab is clicked
  const [voicePromptFetched, setVoicePromptFetched] = useState(false);
  useEffect(() => {
    if (activeTab === 1 && !voicePromptFetched) {
      dispatch(getDefaultVoicePrompt());
      setVoicePromptFetched(true);
    }
  }, [activeTab, voicePromptFetched, dispatch]);

  // Update simple prompt text when data is fetched
  useEffect(() => {
    const payload = prompt?.data?.data || prompt?.data || {};
    const existingPrompt = payload?.prompt || "";
    setPromptText(existingPrompt);
  }, [prompt]);

  // Update voice prompt text when data is fetched
  useEffect(() => {
    const payload = defaultVoicePrompt?.data?.data || defaultVoicePrompt?.data || {};
    const existingPrompt = payload?.prompt || "";
    setVoicePromptText(existingPrompt);
  }, [defaultVoicePrompt]);

  const loadingSimplePrompt = prompt.loading;
  const loadingVoicePrompt = defaultVoicePrompt.loading;
  const savingSimple = globalPrompt.loading;
  const savingVoice = globalVoicePrompt.loading;

  // Check if text field has content
  const hasSimpleContent = useMemo(() => {
    return promptText.trim().length > 0;
  }, [promptText]);

  const hasVoiceContent = useMemo(() => {
    return voicePromptText.trim().length > 0;
  }, [voicePromptText]);

  const handleSave = () => {
    setConfirmDialogOpen(true);
    setConfirmationText("");
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    
    if (activeTab === 0) {
      // Simple Prompt
      await dispatch(saveGlobalPrompt(promptText, "simpleprompt"));
    } else {
      // Voice Prompt
      await dispatch(saveGlobalVoicePrompt(voicePromptText, "voiceprompt"));
    }
    setConfirmationText("");
  };

  const handleCancelSave = () => {
    setConfirmDialogOpen(false);
    setConfirmationText("");
  };

  const isConfirmDisabled = confirmationText !== "I'll confirm this";

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const currentLoading = activeTab === 0 ? loadingSimplePrompt : loadingVoicePrompt;
  const currentSaving = activeTab === 0 ? savingSimple : savingVoice;
  const currentHasContent = activeTab === 0 ? hasSimpleContent : hasVoiceContent;

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Global Prompt Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 1 }}>
            Configure the global prompt settings that will apply to all upcoming new agents.
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2, border: "1px solid #e5e7eb" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="prompt tabs"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Simple Prompt" />
            <Tab label="Voice Prompt" />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {/* Simple Prompt Tab */}
          <Paper sx={{ p: 2, border: "1px solid #e5e7eb" }}>
            <Box sx={{ position: "relative" }}>
              {loadingSimplePrompt && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.6)",
                    zIndex: 2,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={20}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="Enter global prompt settings here..."
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "monospace",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={savingSimple || !hasSimpleContent}
                sx={{ textTransform: "none" }}
              >
                {savingSimple ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Voice Prompt Tab */}
          <Paper sx={{ p: 2, border: "1px solid #e5e7eb" }}>
            <Box sx={{ position: "relative" }}>
              {loadingVoicePrompt && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.6)",
                    zIndex: 2,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={20}
                value={voicePromptText}
                onChange={(e) => setVoicePromptText(e.target.value)}
                placeholder="Enter voice prompt settings here..."
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "monospace",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={savingVoice || !hasVoiceContent}
                sx={{ textTransform: "none" }}
              >
                {savingVoice ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        {/* Confirmation Dialog with Text Input */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelSave}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to save these {activeTab === 0 ? "simple" : "voice"} prompt settings? This
              will update the global prompt configuration for all agents.
            </DialogContentText>
            <DialogContentText sx={{ mb: 2, fontWeight: 600 }}>
              Please type "I'll confirm this" below to enable the confirm
              button:
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              placeholder="Type: I'll confirm this"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              disabled={currentSaving}
              variant="outlined"
              size="small"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSave} disabled={currentSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              variant="contained"
              disabled={currentSaving || isConfirmDisabled}
              sx={{ textTransform: "none" }}
            >
              {currentSaving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Confirm Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
